import { Capacitor, registerPlugin, SystemBars, SystemBarsStyle } from "@capacitor/core";
import { observeNativeHealth } from "@/shared/observability/performance";
import packageMetadata from "../../../package.json";

type NativeReply = {
  status: number;
  headers: Record<string, string>;
  body: string;
};
export type NativeInfo = {
  platform: "android" | "windows";
  origin: string;
  authenticated: boolean;
  authStatus: string;
  localDevelopment: boolean;
  packageVersion?: string;
  foreground?: boolean;
  authError?: string;
};
type NativeCommand =
  | "info"
  | "login"
  | "cancel_login"
  | "request"
  | "logout"
  | "external"
  | "offline_encrypt"
  | "offline_decrypt"
  | "offline_clear"
  | "share_read"
  | "share_file_chunk"
  | "share_clear"
  | "notification_status"
  | "notification_request"
  | "notification_deliver"
  | "appearance";
type NativeArgs = Record<string, unknown>;
type TauriWindow = Window & {
  __TAURI__?: {
    core: { invoke: <T>(command: string, args: NativeArgs) => Promise<T> };
  };
};
const android = registerPlugin<{
  execute<T>(options: { command: NativeCommand; args: NativeArgs }): Promise<T>;
}>("ExognitosNative");
let lastNativeAuthStatus: string | undefined;
let nativeHealthStarted = false;

function observeNativeInfo(info: NativeInfo) {
  if (lastNativeAuthStatus === "waiting_for_browser") {
    if (info.authenticated || info.authStatus === "authenticated") {
      observeNativeHealth(info.platform, "oauth_callback", "success");
    } else if (
      info.authStatus === "authorization_failed" ||
      info.authStatus === "reauthorization_required"
    ) {
      observeNativeHealth(info.platform, "oauth_callback", "failure");
    }
  }
  lastNativeAuthStatus = info.authStatus;
}

export function isNative(): boolean {
  return Capacitor.isNativePlatform() || Boolean((window as TauriWindow).__TAURI__?.core);
}

export function isAndroidNative(): boolean {
  return Capacitor.isNativePlatform() && Capacitor.getPlatform() === "android";
}

export type NativeNotificationPermission = "default" | "denied" | "granted" | "unsupported";

export type NativeReminderNotification = {
  id: string;
  revision: number;
  title: string;
  body: string;
  source_type?: string | null;
  source_id?: string | null;
  task_id?: string | null;
  quick_actions?: Array<{
    id: string;
    method: string;
    path: string;
    body: Record<string, unknown>;
    required_fields?: string[];
    defaults?: Record<string, unknown>;
    requires_authenticated_review: boolean;
  }>;
};

async function invoke<T>(command: NativeCommand, args: NativeArgs = {}): Promise<T> {
  const tauri = (window as TauriWindow).__TAURI__;
  if (tauri) return tauri.core.invoke<T>("native_execute", { command, args });
  if (Capacitor.isNativePlatform()) return android.execute<T>({ command, args });
  throw new Error("Native transport is unavailable in the browser.");
}

export async function nativeInfo() {
  initializeNativeShell();
  const info = await invoke<NativeInfo>("info");
  observeNativeInfo(info);
  return info;
}
export async function nativeLogin(origin: string) {
  try {
    const info = await invoke<NativeInfo>("login", {
      origin,
      locale: document.documentElement.lang === "tr" ? "tr" : "en",
    });
    observeNativeInfo(info);
    return info;
  } catch (error) {
    const platform = Capacitor.getPlatform() === "android" ? "android" : "windows";
    observeNativeHealth(platform, "oauth_callback", "failure");
    throw error;
  }
}
export async function nativeCancelLogin() {
  const info = await invoke<NativeInfo>("cancel_login");
  if (lastNativeAuthStatus === "waiting_for_browser") {
    observeNativeHealth(info.platform, "oauth_callback", "cancelled");
  }
  lastNativeAuthStatus = info.authStatus;
  return info;
}

function notificationPermission(value: unknown): NativeNotificationPermission {
  if (value === "granted" || value === "denied") return value;
  if (value === "prompt") return "default";
  return "unsupported";
}

/** Android permission requests are made only from the notification-center user gesture. */
export async function nativeNotificationPermission(
  request = false,
): Promise<NativeNotificationPermission> {
  if (!isAndroidNative()) return "unsupported";
  const reply = await invoke<{ permission?: unknown }>(
    request ? "notification_request" : "notification_status",
  );
  return notificationPermission(reply.permission);
}

/** Deliver server-issued reminder metadata; Android independently revalidates every action. */
export async function nativeDeliverReminder(reminder: NativeReminderNotification) {
  if (!isAndroidNative()) return { delivered: false, permission: "unsupported" as const };
  const reply = await invoke<{ delivered?: unknown; permission?: unknown }>(
    "notification_deliver",
    {
      reminder,
    },
  );
  return {
    delivered: reply.delivered === true,
    permission: notificationPermission(reply.permission),
  };
}

export async function startNativeHealthTelemetry() {
  if (nativeHealthStarted || !isNative()) return;
  nativeHealthStarted = true;
  try {
    const info = await nativeInfo();
    observeNativeHealth(info.platform, "startup", "success");
    const versionOutcome = !info.packageVersion
      ? "missing"
      : info.packageVersion === packageMetadata.version
        ? "current"
        : "required";
    observeNativeHealth(info.platform, "package_version", versionOutcome);
    observeNativeHealth(info.platform, "update", versionOutcome);
  } catch {
    const platform = Capacitor.getPlatform() === "android" ? "android" : "windows";
    observeNativeHealth(platform, "startup", "failure");
    observeNativeHealth(platform, "package_version", "unknown");
    observeNativeHealth(platform, "update", "unknown");
  }
}

export function resetNativeHealthForTests() {
  lastNativeAuthStatus = undefined;
  nativeHealthStarted = false;
}

export const DEFAULT_NATIVE_ORIGIN = "https://exognitos.com";
const LOGIN_FAILURES = [
  "origin",
  "discovery",
  "server",
  "callback_listener",
  "registration",
  "secure_storage",
  "browser",
  "callback_timeout",
  "authorization",
  "exchange",
  "connection",
] as const;
export type NativeLoginFailure = (typeof LOGIN_FAILURES)[number];

/** Never render arbitrary host/plugin/server errors; only known stage codes cross the UI. */
export function nativeLoginFailure(error: unknown): NativeLoginFailure {
  const value = typeof error === "string" ? error : error instanceof Error ? error.message : "";
  const code = value.replace(/^native\./, "");
  return LOGIN_FAILURES.find((candidate) => candidate === code) ?? "connection";
}
export function nativeLogout() {
  return invoke<{ ok: boolean }>("logout");
}
export function nativeOpenExternal(url: string) {
  return invoke<{ ok: boolean }>("external", { url });
}

type NativeCiphertext = { iv: string; ciphertext: string };

function decode(value: string): ArrayBuffer {
  return Uint8Array.from(atob(value), (character) => character.charCodeAt(0)).buffer;
}

/** Seal offline bytes with an account-scoped key that remains in the OS vault. */
export async function nativeEncryptOffline(
  accountId: string,
  additionalData: Uint8Array,
  plaintext: Uint8Array,
): Promise<{ iv: ArrayBuffer; ciphertext: ArrayBuffer }> {
  const result = await invoke<NativeCiphertext>("offline_encrypt", {
    accountId,
    additionalData: encode(additionalData),
    plaintext: encode(plaintext),
  });
  return { iv: decode(result.iv), ciphertext: decode(result.ciphertext) };
}

/** Open bytes only when both the OS-vault key and authenticated metadata match. */
export async function nativeDecryptOffline(
  accountId: string,
  additionalData: Uint8Array,
  iv: ArrayBuffer,
  ciphertext: ArrayBuffer,
): Promise<ArrayBuffer> {
  const result = await invoke<{ plaintext: string }>("offline_decrypt", {
    accountId,
    additionalData: encode(additionalData),
    iv: encode(new Uint8Array(iv)),
    ciphertext: encode(new Uint8Array(ciphertext)),
  });
  return decode(result.plaintext);
}

export function nativeClearOfflineKey(accountId: string): Promise<{ ok: boolean }> {
  return invoke<{ ok: boolean }>("offline_clear", { accountId });
}

export type NativeExternalShareMetadata = {
  schemaVersion: 1;
  source: "android";
  id: string;
  draftId: string;
  idempotencyKey: string;
  title: string;
  text: string;
  url: string;
  createdAt: number;
  files: Array<{
    id: string;
    name: string;
    type: string;
    size: number;
    lastModified: number;
    uploadAttempt: number;
  }>;
};

export type NativeExternalShare = Omit<NativeExternalShareMetadata, "files"> & {
  files: Array<NativeExternalShareMetadata["files"][number] & { bytes: ArrayBuffer }>;
};

const NATIVE_SHARE_CHUNK_BYTES = 256 * 1024;
const NATIVE_SHARE_MAX_FILES = 4;
const NATIVE_SHARE_MAX_FILE_BYTES = 8 * 1024 * 1024;
const NATIVE_SHARE_MAX_TOTAL_BYTES = 24 * 1024 * 1024;

/** Read the app-private copy in bounded chunks; external content URIs never enter the WebView. */
export async function nativeReadExternalShare(): Promise<NativeExternalShare | null> {
  const pending = await nativeReadExternalShareMetadata();
  if (!pending) return null;
  if (!Array.isArray(pending.files) || pending.files.length > NATIVE_SHARE_MAX_FILES) {
    throw new Error("native.external_share_invalid");
  }
  let totalSize = 0;
  for (const metadata of pending.files) {
    if (
      !Number.isSafeInteger(metadata.size) ||
      metadata.size <= 0 ||
      metadata.size > NATIVE_SHARE_MAX_FILE_BYTES
    ) {
      throw new Error("native.external_share_invalid");
    }
    totalSize += metadata.size;
  }
  if (totalSize > NATIVE_SHARE_MAX_TOTAL_BYTES) {
    throw new Error("native.external_share_invalid");
  }
  const files: NativeExternalShare["files"] = [];
  for (const metadata of pending.files) {
    const chunks: Uint8Array[] = [];
    let offset = 0;
    while (offset < metadata.size) {
      const chunk = await invoke<{ data: string; nextOffset: number; done: boolean }>(
        "share_file_chunk",
        {
          shareId: pending.id,
          fileId: metadata.id,
          offset,
          maximum: NATIVE_SHARE_CHUNK_BYTES,
        },
      );
      const maximumEncodedLength = Math.ceil(NATIVE_SHARE_CHUNK_BYTES / 3) * 4 + 4;
      if (typeof chunk.data !== "string" || chunk.data.length > maximumEncodedLength) {
        throw new Error("native.external_share_invalid");
      }
      const bytes = new Uint8Array(decode(chunk.data));
      if (
        bytes.byteLength === 0 ||
        !Number.isSafeInteger(chunk.nextOffset) ||
        chunk.nextOffset !== offset + bytes.byteLength ||
        chunk.nextOffset > metadata.size ||
        chunk.done !== (chunk.nextOffset === metadata.size)
      ) {
        throw new Error("native.external_share_invalid");
      }
      chunks.push(bytes);
      offset = chunk.nextOffset;
    }
    const bytes = new Uint8Array(metadata.size);
    let position = 0;
    for (const chunk of chunks) {
      bytes.set(chunk, position);
      position += chunk.byteLength;
    }
    files.push({ ...metadata, bytes: bytes.buffer });
  }
  return { ...pending, files };
}

/** Read only URI-free private metadata when deciding whether cleanup is safe. */
export async function nativeReadExternalShareMetadata(): Promise<NativeExternalShareMetadata | null> {
  if (!isAndroidNative()) return null;
  const reply = await invoke<{ pending: NativeExternalShareMetadata | null }>("share_read");
  return reply.pending;
}

export function nativeClearExternalShare(id: string): Promise<{ ok: boolean }> {
  return invoke<{ ok: boolean }>("share_clear", { id });
}

let nativeShellInitialized = false;
function initializeNativeShell() {
  if (nativeShellInitialized || !isNative()) return;
  nativeShellInitialized = true;
  let previousDark: boolean | undefined;
  const synchronizeAppearance = () => {
    const dark = getComputedStyle(document.documentElement).colorScheme.includes("dark");
    if (dark === previousDark) return;
    previousDark = dark;
    const update = Capacitor.isNativePlatform()
      ? SystemBars.setStyle({
          style: dark ? SystemBarsStyle.Dark : SystemBarsStyle.Light,
        })
      : invoke("appearance", { dark });
    void update.catch(() => {
      /* A platform appearance failure must not block account access. */
    });
  };
  new MutationObserver(synchronizeAppearance).observe(document.documentElement, {
    attributes: true,
    attributeFilter: ["data-theme", "style"],
  });
  synchronizeAppearance();
  document.addEventListener("click", (event) => {
    const link = (event.target as Element | null)?.closest<HTMLAnchorElement>("a[href]");
    if (!link || link.hasAttribute("download")) return;
    const url = new URL(link.href, window.location.href);
    if (url.origin === window.location.origin || !["https:", "http:"].includes(url.protocol))
      return;
    event.preventDefault();
    void nativeOpenExternal(url.href).catch(() => {
      /* The OS refuses unsupported external targets. */
    });
  });
}

/** Only the documented read-only equivalent has the identical SessionOut shape. */
export function nativeApiPath(url: URL, method: string): string {
  if (url.origin !== window.location.origin || !url.pathname.startsWith("/api/v4/")) {
    throw new Error("Native requests must target the application's API paths.");
  }
  const decoded = decodeURIComponent(url.pathname);
  if (decoded.includes("\\") || decoded.split("/").some((part) => part === ".." || part === ".")) {
    throw new Error("Invalid native API path.");
  }
  const path =
    method.toUpperCase() === "GET" && url.pathname === "/api/v4/me"
      ? "/api/v4/auth/whoami"
      : url.pathname;
  return path + url.search;
}

function encode(bytes: Uint8Array): string {
  let result = "";
  for (let offset = 0; offset < bytes.length; offset += 8192) {
    result += String.fromCharCode(...bytes.subarray(offset, offset + 8192));
  }
  return btoa(result);
}

/** Native owns TLS, access/refresh credentials, refresh serialization and the OS vault. */
export async function nativeFetch(input: RequestInfo | URL, init?: RequestInit): Promise<Response> {
  if (!isNative()) return fetch(input, init);
  initializeNativeShell();
  const request = new Request(input, init);
  const url = new URL(request.url, window.location.origin);
  const path = nativeApiPath(url, request.method);
  const fileTransfer =
    url.pathname.startsWith("/api/v4/files") || url.pathname.startsWith("/api/v4/backup");
  if (request.method === "POST" && url.pathname === "/api/v4/auth/logout") {
    await nativeLogout();
    return Response.json({ ok: true });
  }
  const headers: Record<string, string> = {};
  for (const name of ["Content-Type", "Accept", "If-Match", "Idempotency-Key"]) {
    const value = request.headers.get(name);
    if (value) headers[name] = value;
  }
  const body = request.body ? encode(new Uint8Array(await request.arrayBuffer())) : null;
  if (request.signal.aborted) {
    if (fileTransfer)
      observeNativeHealth(
        Capacitor.getPlatform() === "android" ? "android" : "windows",
        "file_transfer",
        "cancelled",
      );
    throw new DOMException("The request was cancelled.", "AbortError");
  }
  let reply: NativeReply;
  try {
    reply = await invoke<NativeReply>("request", {
      path,
      method: request.method,
      headers,
      body,
    });
  } catch (error) {
    if (fileTransfer)
      observeNativeHealth(
        Capacitor.getPlatform() === "android" ? "android" : "windows",
        "file_transfer",
        "failure",
      );
    throw error;
  }
  if (request.signal.aborted) {
    if (fileTransfer)
      observeNativeHealth(
        Capacitor.getPlatform() === "android" ? "android" : "windows",
        "file_transfer",
        "cancelled",
      );
    throw new DOMException("The request was cancelled.", "AbortError");
  }
  if (fileTransfer) {
    observeNativeHealth(
      Capacitor.getPlatform() === "android" ? "android" : "windows",
      "file_transfer",
      reply.status >= 200 && reply.status < 400 ? "success" : "failure",
    );
  }
  const bytes = Uint8Array.from(atob(reply.body), (character) => character.charCodeAt(0));
  return new Response(reply.status === 204 || reply.status === 205 ? null : bytes, {
    status: reply.status,
    headers: reply.headers,
  });
}
